/**
 * CZNA-1563 — a minimal line diff, for showing what one form-template version
 * changed against another.
 *
 * Myers' O(ND) algorithm ("An O(ND) Difference Algorithm and Its Variations",
 * 1986): the shortest edit script, which for two pretty-printed JSON documents is
 * what a reader expects — a changed property shows as one removed and one added
 * line, not as a reshuffle of the identical `},` lines around it. The common
 * prefix and suffix are trimmed first, so the quadratic part of the trace only
 * ever covers the region between the first and the last difference; for the
 * history of a form template (hundreds to low thousands of lines, a handful of
 * edits per version) that is a few kilobytes of work.
 *
 * Written here rather than pulled in as a dependency: the module needs exactly
 * this one function, and it is small enough to test exhaustively (lineDiff.test.ts).
 *
 * <b>The cap (CZNA-1563 verify).</b> The trace costs O(D²) memory and O((N+M)·D)
 * time, all on the UI thread, so two versions that share almost nothing — a
 * restore across a rewrite, a template replaced by another — would allocate
 * hundreds of megabytes and freeze the page. Past {@link MAX_EDIT_DISTANCE} the
 * search stops and the differing region (between the common prefix and suffix)
 * is reported as removed then added, with `truncated: true` so the reader is told
 * the detailed view was given up rather than shown a misleading "minimal" diff.
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * The largest edit distance (removed + added lines) searched for a minimal diff:
 * 2000. The biggest packaged form template is under 900 lines pretty-printed, so
 * even two of them with no line in common (D ≤ ~1800) still get the exact diff;
 * at the cap the trace is ~D² = 4 M 32-bit ints (~16 MB) and a few million
 * steps — tens of milliseconds. Beyond it the cost grows quadratically for a
 * diff nobody can read line by line anyway.
 */
export var MAX_EDIT_DISTANCE = 2000;
/**
 * The edit script for the middle region, as ops over a[] / b[] in order, or
 * `null` when the edit distance exceeds `maxD`.
 */
var myers = function (a, b, maxD) {
    var n = a.length;
    var m = b.length;
    // One side empty: the script is trivial, but the cap still applies — it bounds
    // what the reader is asked to look at and what the page has to render, not
    // only the search (D = the other side's length here).
    if (n === 0)
        return m > maxD ? null : Array(m).fill('add');
    if (m === 0)
        return n > maxD ? null : Array(n).fill('remove');
    var max = n + m;
    var offset = max;
    var v = new Int32Array(2 * max + 2);
    // trace[d] holds v[k] for k in [-d, d] as it stood at the START of round d —
    // only that window is ever read back, so storing it alone keeps the memory at
    // O(D²) instead of O(D·(N+M)).
    var trace = [];
    var found = false;
    for (var d = 0; d <= max && !found; d++) {
        if (d > maxD)
            return null;
        trace.push(v.slice(offset - d, offset + d + 1));
        for (var k = -d; k <= d; k += 2) {
            var x_1 = (k === -d || (k !== d && v[offset + k - 1] < v[offset + k + 1]))
                ? v[offset + k + 1]
                : v[offset + k - 1] + 1;
            var y_1 = x_1 - k;
            while (x_1 < n && y_1 < m && a[x_1] === b[y_1]) {
                x_1++;
                y_1++;
            }
            v[offset + k] = x_1;
            if (x_1 >= n && y_1 >= m) {
                found = true;
                break;
            }
        }
    }
    var ops = [];
    var x = n;
    var y = m;
    var _loop_1 = function (d) {
        var vd = trace[d];
        var at = function (k) { return vd[k + d]; };
        var k = x - y;
        var prevK = (k === -d || (k !== d && at(k - 1) < at(k + 1))) ? k + 1 : k - 1;
        var prevX = d === 0 ? 0 : at(prevK);
        var prevY = d === 0 ? 0 : prevX - prevK;
        while (x > prevX && y > prevY) {
            ops.push('equal');
            x--;
            y--;
        }
        if (d > 0) {
            if (x === prevX) {
                ops.push('add');
                y--;
            }
            else {
                ops.push('remove');
                x--;
            }
        }
    };
    for (var d = trace.length - 1; d >= 0; d--) {
        _loop_1(d);
    }
    return ops.reverse();
};
/**
 * Line diff of `oldText` → `newText`. Lines are split on `\n`; an empty text is
 * one empty line, exactly as `String.prototype.split` sees it, so the two sides
 * always reconstruct their inputs — truncated or not.
 */
export var diffLines = function (oldText, newText, maxEditDistance) {
    if (maxEditDistance === void 0) { maxEditDistance = MAX_EDIT_DISTANCE; }
    var a = oldText.split('\n');
    var b = newText.split('\n');
    var prefix = 0;
    while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix])
        prefix++;
    var suffix = 0;
    while (suffix < a.length - prefix
        && suffix < b.length - prefix
        && a[a.length - 1 - suffix] === b[b.length - 1 - suffix])
        suffix++;
    var midA = a.slice(prefix, a.length - suffix);
    var midB = b.slice(prefix, b.length - suffix);
    var middle = myers(midA, midB, maxEditDistance);
    var ops = __spreadArray(__spreadArray(__spreadArray([], Array(prefix).fill('equal'), true), (middle !== null && middle !== void 0 ? middle : __spreadArray(__spreadArray([], Array(midA.length).fill('remove'), true), Array(midB.length).fill('add'), true)), true), Array(suffix).fill('equal'), true);
    var lines = [];
    var i = 0;
    var j = 0;
    ops.forEach(function (op) {
        if (op === 'equal') {
            lines.push({ op: op, text: a[i], oldLine: i + 1, newLine: j + 1 });
            i++;
            j++;
        }
        else if (op === 'remove') {
            lines.push({ op: op, text: a[i], oldLine: i + 1, newLine: null });
            i++;
        }
        else {
            lines.push({ op: op, text: b[j], oldLine: null, newLine: j + 1 });
            j++;
        }
    });
    return { lines: lines, truncated: middle === null };
};
export var diffStats = function (lines) { return ({
    added: lines.filter(function (l) { return l.op === 'add'; }).length,
    removed: lines.filter(function (l) { return l.op === 'remove'; }).length,
}); };
/**
 * Fold every run of unchanged lines down to `context` lines next to each change,
 * the way a unified diff does. A run that would hide fewer than two lines is
 * left alone — a "1 line hidden" marker takes the same room as the line.
 */
export var collapseUnchanged = function (lines, context) {
    if (context === void 0) { context = 3; }
    var items = [];
    var idx = 0;
    while (idx < lines.length) {
        if (lines[idx].op !== 'equal') {
            items.push({ kind: 'line', line: lines[idx] });
            idx++;
        }
        else {
            var end = idx;
            while (end < lines.length && lines[end].op === 'equal')
                end++;
            var run = lines.slice(idx, end);
            var keepHead = idx === 0 ? 0 : context;
            var keepTail = end === lines.length ? 0 : context;
            var hidden = run.length - keepHead - keepTail;
            if (hidden < 2) {
                run.forEach(function (line) { return items.push({ kind: 'line', line: line }); });
            }
            else {
                run.slice(0, keepHead).forEach(function (line) { return items.push({ kind: 'line', line: line }); });
                items.push({ kind: 'collapsed', count: hidden });
                run.slice(run.length - keepTail).forEach(function (line) { return items.push({ kind: 'line', line: line }); });
            }
            idx = end;
        }
    }
    return items;
};
